<template>
  <div class="lg-stat">
    <div class="lg-stat-label">{{ label }}</div>
    <div class="lg-stat-row">
      <span class="lg-stat-value">{{ displayValue }}</span>
      <span v-if="hasDelta" class="lg-stat-delta" :class="`tone-${deltaTone}`">
        <component :is="deltaIcon" :size="14" aria-hidden="true" />
        <span>{{ deltaText }}</span>
      </span>
    </div>
    <LgSparkline
      v-if="spark && spark.length"
      class="lg-stat-spark"
      :points="spark"
      :tone="sparkTone"
      :width="sparkWidth"
      :height="24"
      fill
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ArrowUp, ArrowDown, Minus } from 'lucide-vue-next';
import LgSparkline from './LgSparkline.vue';
import { formatMetric, formatPercent, type MetricUnit } from '../lib/format-metric.js';

type DeltaTone = 'auto' | 'up' | 'down' | 'flat' | 'ok' | 'warn' | 'danger' | 'neutral';

const props = withDefaults(
  defineProps<{
    label: string;
    /** Number (formatted via `unit`) or a pre-formatted string. */
    value: number | string;
    unit?: MetricUnit;
    /** Percent change vs the window start. Sign drives the arrow + color. */
    delta?: number;
    /** Override the delta text (default: signed percent, e.g. "↑ 34%"). */
    deltaLabel?: string;
    /**
     * Delta color. `auto` follows the spec convention (rising = warn, falling =
     * ok, flat = neutral); override for metrics where up is good/bad.
     */
    deltaTone?: DeltaTone;
    /** Optional inline sparkline data. */
    spark?: number[] | Array<[number, number]>;
    sparkTone?: 'auto' | 'up' | 'down' | 'neutral' | 'accent';
    sparkWidth?: number;
  }>(),
  { unit: 'none', deltaTone: 'auto', sparkTone: 'auto', sparkWidth: 96 },
);

const displayValue = computed(() =>
  typeof props.value === 'number' ? formatMetric(props.value, props.unit) : props.value,
);

const hasDelta = computed(() => props.delta != null && Number.isFinite(props.delta));

const direction = computed<'up' | 'down' | 'flat'>(() => {
  const d = props.delta ?? 0;
  if (d > 0) return 'up';
  if (d < 0) return 'down';
  return 'flat';
});

const deltaIcon = computed(() =>
  direction.value === 'up' ? ArrowUp : direction.value === 'down' ? ArrowDown : Minus,
);

const deltaText = computed(() => {
  if (props.deltaLabel) return props.deltaLabel;
  if (direction.value === 'flat') return 'flat';
  return formatPercent(Math.abs(props.delta ?? 0));
});

// Resolve the semantic color class. `auto` maps direction → spec convention.
const deltaTone = computed(() => {
  if (props.deltaTone !== 'auto') return props.deltaTone;
  if (direction.value === 'up') return 'warn';
  if (direction.value === 'down') return 'ok';
  return 'neutral';
});
</script>

<style scoped>
.lg-stat {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
  min-width: 0;
  padding: var(--lg-space-3);
  background: var(--lg-bg-raised);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
}
.lg-stat-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lg-stat-row {
  display: flex;
  align-items: baseline;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
}
.lg-stat-value {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-2xl);
  line-height: var(--lg-lh-tight);
  color: var(--lg-text);
}
.lg-stat-delta {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
}
.lg-stat-delta.tone-up,
.lg-stat-delta.tone-warn {
  color: var(--lg-warn);
}
.lg-stat-delta.tone-down,
.lg-stat-delta.tone-ok {
  color: var(--lg-ok);
}
.lg-stat-delta.tone-danger {
  color: var(--lg-danger);
}
.lg-stat-delta.tone-flat,
.lg-stat-delta.tone-neutral {
  color: var(--lg-text-faint);
}
.lg-stat-spark {
  margin-top: var(--lg-space-1);
}
</style>
